import Box from "@mui/material/Box";
import ReactEcharts from "echarts-for-react";
import { forwardRef } from "react";
import NoDataAvailable from "../../components/NoDataAvailable";
import { STIM_CHANNEL_FIELDS, buildStimSeries, formatStimTooltipValue } from "../../utils/stimSeries";

const GAIT_LINE_WIDTH = 4;

const stimSeries = (name, data, color, lineType) => ({
    name: name,
    data: data,
    type: 'line',
    step: 'end',
    itemStyle: {
        color: color,
    },
    lineStyle: {
        color: color,
        width: GAIT_LINE_WIDTH / 2,
        type: lineType,
    },
    symbol: 'none',
    tooltip: {
        valueFormatter: formatStimTooltipValue,
    },
});

const GaitCycleChart = forwardRef((props, ref) => {
    const { rawData, group } = props;

    if (!rawData || rawData.length === 0) {
        return (
            <NoDataAvailable />
        );
    }

    const timeSeries = rawData.map(item => item.ms / 1000);
    const GaitCycle = rawData.map(item => item.gaitVals_percent);

    const minTime = Math.min(...timeSeries);
    const normalizedTimeSeries = timeSeries.map(time => time - minTime)
    const interval = Math.floor(normalizedTimeSeries.length / 10);

    // FW 1.0.8 trials have no stim fields: these stay null and the chart is gait-only.
    const ch1Stim = buildStimSeries(rawData, STIM_CHANNEL_FIELDS.ch1);
    const ch2Stim = buildStimSeries(rawData, STIM_CHANNEL_FIELDS.ch2);
    const hasStim = ch1Stim !== null || ch2Stim !== null;

    const option = {
        group: group,
        title: {
            text: 'Gait Cycle',
            left: 'left',
            padding: 10,
        },
        color: ['#9FD3D3'],
        tooltip: {
            trigger: 'axis',
        },
        // Wider legend icons so the dashed/dotted stim strokes are readable.
        legend: hasStim ? { itemWidth: 40 } : {},
        xAxis: {
            type: 'category',
            axisLabel: {
                interval: interval,
                formatter: function (value) {
                    return parseFloat(value).toFixed(2);
                }
            },
            data: normalizedTimeSeries || [],
            name: 'Time',
            nameTextStyle: {
                fontSize: 15,
            },
            axisLine: {
                lineStyle: {
                    color: '#000000',
                },
            },
        },
        yAxis: {
            type: 'value',
            name: '',
            // Pin to 0-100 so stim on (100) sits at the top; gait-only keeps auto scale.
            ...(hasStim && { min: 0, max: 100 }),
            nameTextStyle: {
                fontSize: 15,
            },
            axisLine: {
                lineStyle: {
                    color: '#000000',
                },
            },
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: {
                    show: true,
                    title: 'Save As Image',
                    name: 'Gait Cycle',
                    excludeComponents: ['toolbox'],
                    pixelRatio: 2,
                },
            },
        },
        dataZoom: [
            {
                type: 'slider',
                start: 0,
                end: 100,
                bottom: 10,
                xAxisIndex: [0],
                borderColor: '#68B3C8',
                handleColor: '#68B3C8',
                handleSize: '80%',
                textStyle: {
                    color: '#333'
                }
            },
            {
                type: 'inside',
                start: 0,
                end: 100,
                xAxisIndex: [0],
            },
        ],
        series: [
            {
                name: 'Gait Cycle',
                data: GaitCycle || [],
                type: 'line',
                smooth: true,
                lineStyle: {
                    normal: {
                        width: GAIT_LINE_WIDTH,
                        type: 'solid',
                    },
                },
                areaStyle: {
                    color: '#E9EFFD',
                },
                symbolSize: 6,
            },
            ...(ch1Stim ? [stimSeries('CH1 stim on', ch1Stim, '#D0312D', 'dashed')] : []),
            ...(ch2Stim ? [stimSeries('CH2 stim on', ch2Stim, '#E6B800', 'dotted')] : []),
        ],
    };

    return (
        <Box width="80%" height="400px" borderRadius={3} boxShadow={5} p={1}>
            <ReactEcharts 
                ref={ref} 
                option={option} 
                style={{ height: '100%' }}
                opts={{ renderer: 'canvas' }}
            />
        </Box>
    );
});

export default GaitCycleChart;
